import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getUserData } from "@/lib/api";
import { Navigation } from "@/components/Navigation";
import { DnaBackground } from "@/components/DnaBackground";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";
import AdminUserManagement from "@/components/AdminUserManagement";
import AdminAnalytics from "@/components/AdminAnalytics";
import AdminPDFReports from "@/components/AdminPDFReports";
import AdminStrengthsLeaderboard from "@/components/AdminStrengthsLeaderboard";
import { Dashboard } from "@/components/Dashboard";
import { useAdminAnalytics } from "@/hooks/use-admin-analytics";
import {
  Users,
  BarChart3,
  TrendingUp,
  Activity,
  Upload,
  Trophy,
} from "lucide-react";

export default function Admin() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [user, setUser] = useState<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const allowedTabs = ["users", "pdfreports", "leaderboard", "analytics"];
  const activeTab = allowedTabs.includes(tabParam || "") ? tabParam! : "users";

  const handleTabChange = (value: string) => {
    if (value === "users") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  };

  const adminTabs = [
    { value: "users", label: t('admin.users'), icon: Users },
    { value: "pdfreports", label: t('pdfReports.title'), icon: Upload },
    { value: "leaderboard", label: t('strengthsExplorer.adminTabLabel'), icon: Trophy },
    { value: "analytics", label: t('admin.analytics'), icon: BarChart3 },
  ];
  const orderedTabs = isRtl ? [...adminTabs].reverse() : adminTabs;

  const { data: analyticsData, isLoading: isAnalyticsLoading } = useAdminAnalytics();

  useEffect(() => {
    const userData = getUserData();
    setUser(userData);
  }, []);

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center">
      <DnaBackground />
      Loading...
    </div>;
  }

  return (
    <div className="min-h-screen">
      <DnaBackground />
      <Navigation user={user} />

      <main className="pt-20 px-4 pb-10 sm:pt-24 sm:px-6 sm:pb-12 overflow-x-hidden" dir={isRtl ? "rtl" : "ltr"}>
        <div className="container mx-auto max-w-7xl min-w-0">
          <div className="mb-6 animate-fade-in sm:mb-8">
            <h1 className="text-3xl font-bold mb-2 sm:text-4xl">{t('admin.title')}</h1>
            <p className="text-base text-muted-foreground sm:text-xl">
              {t('admin.subtitle')}
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 md:grid-cols-3 sm:gap-6 sm:mb-8">
            <Card className="p-4 bg-card border-border animate-scale-in sm:p-6">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-primary/20 flex shrink-0 items-center justify-center sm:w-12 sm:h-12">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-bold text-foreground break-words">
                    {isAnalyticsLoading ? '...' : analyticsData?.totalUsers.toLocaleString() || '0'}
                  </div>
                  <div className="text-sm text-muted-foreground">{t('admin.totalUsers')}</div>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-card border-border animate-scale-in sm:p-6" style={{ animationDelay: "0.1s" }}>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-secondary/20 flex shrink-0 items-center justify-center sm:w-12 sm:h-12">
                  <Activity className="w-6 h-6 text-secondary" />
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-bold text-foreground break-words">
                    {isAnalyticsLoading ? '...' : `${analyticsData?.completionRate || 0}%`}
                  </div>
                  <div className="text-sm text-muted-foreground">{t('admin.completionRate')}</div>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-card border-border animate-scale-in sm:p-6" style={{ animationDelay: "0.2s" }}>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-accent/20 flex shrink-0 items-center justify-center sm:w-12 sm:h-12">
                  <TrendingUp className="w-6 h-6 text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-bold text-foreground break-words">
                    {isAnalyticsLoading ? '...' : analyticsData?.growthRate.toLocaleString() || '0'}
                  </div>
                  <div className="text-sm text-muted-foreground">{t('admin.growthRate')}</div>
                </div>
              </div>
            </Card>
          </div>

          <Dashboard audience="admin" />

          {/* Tabs for different sections */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="animate-fade-in min-w-0">
            <div className="-mx-4 mb-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
              <TabsList className="w-max bg-card border border-border">
                {orderedTabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <TabsTrigger key={tab.value} value={tab.value} className="flex shrink-0 items-center gap-2 px-3">
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="whitespace-nowrap">{tab.label}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>

            <TabsContent value="users" className="mt-6">
              <AdminUserManagement />
            </TabsContent>

            <TabsContent value="pdfreports" className="mt-6">
              <AdminPDFReports />
            </TabsContent>

            <TabsContent value="leaderboard" className="mt-6">
              <AdminStrengthsLeaderboard />
            </TabsContent>

            <TabsContent value="analytics" className="mt-6">
              <AdminAnalytics />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
